import { ArrowLeft, Bell, CalendarDays, CheckCircle2, CreditCard, ReceiptText } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { LoadingState } from '../../components/common/LoadingState';
import { getCourse, getPublicAcademyBySlug, getStudentCourseSignups, getStudentTransactions, getTenant } from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { formatCurrency } from '../../utils/currency';
import { formatDate, formatDateTime } from '../../utils/date';
import { getHostnameContext } from '../../utils/hostname';
import { StudentSidebar } from './StudentSidebar';

function asDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function StudentInvoiceViewPage() {
  const { invoiceId } = useParams();
  const { user, loading: authLoading, logout } = useAuth();
  const { notify } = useToast();
  const hostnameContext = useMemo(() => getHostnameContext(), []);
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [academyName, setAcademyName] = useState('Student workspace');
  const [currency, setCurrency] = useState('KES');

  useEffect(() => {
    if (!user?.uid || !invoiceId) return;
    const readKey = `smartcampus:read-invoices:${user.uid}`;
    const readIds = new Set(JSON.parse(localStorage.getItem(readKey) || '[]'));
    readIds.add(invoiceId);
    localStorage.setItem(readKey, JSON.stringify([...readIds]));
    window.dispatchEvent(new Event('student-invoice-read'));
  }, [invoiceId, user?.uid]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!user?.uid || !invoiceId) return;
      try {
        const allSignups = await getStudentCourseSignups(user.uid);
        let scoped = allSignups;
        if (hostnameContext.tenantSlug) {
          const academy = await getPublicAcademyBySlug(hostnameContext.tenantSlug);
          scoped = academy ? allSignups.filter((signup) => signup.tenantId === academy.id) : [];
        }
        const tenantIds = [...new Set(scoped.map((signup) => signup.tenantId).filter(Boolean))];
        const matches = await Promise.all(tenantIds.map(async (tenantId) => {
          const tenantSignups = scoped.filter((signup) => signup.tenantId === tenantId);
          const tenant = await getTenant(tenantId);
          const records = await getStudentTransactions(tenantId, user.uid, user.email || tenantSignups.find((signup) => signup.email || signup.authEmail)?.email || '', tenantSignups.map((signup) => signup.id));
          const record = records.find((item) => item.id === invoiceId && (item.type === 'invoice' || item.category === 'invoice'));
          if (!record) return null;
          const course = record.courseId ? await getCourse(tenantId, record.courseId).catch(() => null) : null;
          return { ...record, tenant, course };
        }));
        const found = matches.find(Boolean) || null;
        if (mounted) {
          setInvoice(found);
          setAcademyName(found?.tenant?.name || 'My learning space');
          setCurrency(found?.tenant?.settings?.currency || 'KES');
          setLoading(false);
        }
      } catch (error) {
        console.error('Unable to load student invoice', error);
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [hostnameContext.tenantSlug, invoiceId, user?.email, user?.uid]);

  async function onLogout() {
    await logout();
    notify('You have been signed out.');
  }

  if (authLoading) return <LoadingState label="Loading invoice..." />;
  if (!user) return <Navigate to="/student/login" replace />;
  const dueDate = asDate(invoice?.dueDate);
  const createdDate = asDate(invoice?.createdAt);
  const status = invoice?.status || 'sent';

  return (
    <main className="student-portal student-invoice-view-page">
      <StudentSidebar academyName={academyName} onLogout={onLogout} />
      <div className="student-invoice-view-shell">
        <section className="student-invoice-view-workspace">
          <header className="student-finance-page-header-new">
            <div><span>Welcome back</span><h1>Invoice details</h1><p>{createdDate ? formatDateTime(createdDate) : 'Student finance'}</p></div>
            <button type="button" className="student-dashboard-notification" aria-label="Notifications"><Bell size={19} strokeWidth={1.7} /><i /></button>
          </header>
          {!loading && invoice ? (
            <div className="student-invoice-detail-card">
              <div className="student-invoice-detail-top"><div><span className="student-invoice-detail-icon"><ReceiptText size={22} /></span><small>Invoice</small><h2>{invoice.invoiceNumber || `INV-${invoice.id.slice(0, 8).toUpperCase()}`}</h2></div><span className={`student-finance-pill-new student-finance-pill-new--${status}`}>{status}</span></div>
              <div className="student-invoice-detail-amount"><small>Amount due</small><strong>{formatCurrency(invoice.amount, currency)}</strong></div>
              <div className="student-invoice-detail-grid">
                <div><small>Course</small><strong>{invoice.courseName || invoice.course?.name || 'Course fee'}</strong></div>
                <div><small>Due date</small><strong>{dueDate ? formatDate(dueDate) : '—'}</strong></div>
                <div><small>Issued by</small><strong>{academyName}</strong></div>
                <div><small>Payment status</small><strong><CheckCircle2 size={15} /> {status}</strong></div>
              </div>
              {invoice.note ? <div className="student-invoice-detail-note"><small>Note from your academy</small><p>{invoice.note}</p></div> : null}
              <div className="student-invoice-detail-actions"><Link to="/student/invoices"><ArrowLeft size={16} /> Back to invoices</Link><Link to="/student/fee-payments"><CreditCard size={16} /> View fee payments</Link></div>
            </div>
          ) : loading ? <div className="student-courses-inline-loading"><span /><p>Loading invoice...</p></div> : <div className="student-courses-empty"><ReceiptText size={25} /><h2>Invoice not found</h2><p>This invoice is not available in your student account.</p><Link to="/student/invoices">Back to invoices</Link></div>}
        </section>
        <aside className="student-invoice-view-aside"><CalendarDays size={21} /><h2>Stay on top of your fees.</h2><p>Review your invoice details and keep your payment history in one clear place.</p><Link to="/student/invoices">View invoice history</Link></aside>
      </div>
    </main>
  );
}
